import { Button } from "@design";
import { useQuery } from "@tanstack/react-query";
import { RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { listMyJobsOptions, listWorkersOptions } from "@/api";
import { Page } from "@/components/Page";
import { PageSection } from "@/components/section/SectionBlocks";
import { SectionError } from "@/components/section/SectionStates";
import { Text } from "@/components/Text";
import { JobsTable, WorkersTable } from "./RallyTables";
import { listOf } from "./rallyStatus";

const WORKERS_REFRESH_MS = 15_000;
const JOBS_REFRESH_MS = 5_000;

export function RallyPage() {
  const { t } = useTranslation("settings");
  const workers = useQuery({ ...listWorkersOptions(), refetchInterval: WORKERS_REFRESH_MS });
  const jobs = useQuery({ ...listMyJobsOptions({ query: { limit: 25 } }), refetchInterval: JOBS_REFRESH_MS });
  const refreshing = workers.isFetching || jobs.isFetching;
  const workerRows = listOf(workers.data);

  return (
    <Page
      title={t("rally.title")}
      subtitle={t("rally.subtitle")}
      actions={
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<RotateCw size={14} className={refreshing ? "animate-spin" : undefined} />}
          onClick={() => {
            void workers.refetch();
            void jobs.refetch();
          }}
        >
          {t("rally.refresh")}
        </Button>
      }
    >
      <PageSection title={t("rally.workers.title")} description={t("rally.workers.description")}>
        {workers.isError ? (
          <SectionError onRetry={() => void workers.refetch()} error={workers.error} />
        ) : (
          <WorkersTable workers={workerRows} loading={workers.isPending} />
        )}
        {workers.isSuccess && workerRows.length === 0 && (
          <Text variant="body-sm" tone="tertiary">
            {t("rally.workers.setup")}{" "}
            <Link to="/settings/security" className="text-content-primary underline">
              {t("rally.workers.setupLink")}
            </Link>
          </Text>
        )}
      </PageSection>
      <PageSection title={t("rally.jobs.title")} description={t("rally.jobs.description")}>
        {jobs.isError ? (
          <SectionError onRetry={() => void jobs.refetch()} error={jobs.error} />
        ) : (
          <JobsTable jobs={listOf(jobs.data)} loading={jobs.isPending} />
        )}
      </PageSection>
    </Page>
  );
}
